import {
  InteractiveContainer,
  ListBox,
  ListBoxItem,
  PlainButton,
  Popover,
  Radio,
  Tooltip,
} from "@jugl-web/ui-components";
import { SearchInput } from "@jugl-web/ui-components/cross-platform/SearchInput";
import {
  cx,
  useAppVariant,
  useDebounce,
  useSearchInput,
  useTranslations,
} from "@jugl-web/utils";
import React, { useEffect, useMemo, useState } from "react";
import {
  CRMChannel,
  CRMConversationFilter,
  CRMConversationMode,
} from "@jugl-web/rest-api/integrations/types";
import {
  twilioMessages$,
  webChatBotMessages$,
  instagramMessages$,
} from "@jugl-web/rest-api";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/hooks/useCRMUnreadIndicators";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { ReactComponent as FilterIcon } from "./assets/filter.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as SupportIcon } from "./assets/support.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as AllChatsIcon } from "./assets/all-chats.svg";
import { ReactComponent as AIIcon } from "./assets/ai.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ConversationTab } from "./components/ConversationTab";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as WebIcon } from "./assets/web.svg";
import { ReactComponent as SmsIcon } from "./assets/sms.svg";
import { ReactComponent as InsightsIcon } from "./assets/insights.svg";
import { WhatsappConversations } from "./components/WhatsappConversations";
import { InstagramConversations } from "./components/InstagramConversations";
import { WebChatBotConversations } from "./components/WebChatBotConversations";
import { TwilioConversations } from "./components/TwilioConversations";
import unreadIndicatorAnimation from "./assets/unread-indicator.json";
import { useManageAccountsDrawer } from "../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

export const ConversationListSidebar: React.FC = () => {
  const { t } = useTranslations();
  const [selectedFilter, setSelectedFilter] = useState<CRMConversationFilter>(
    CRMConversationFilter.active
  );
  const [selectedMode, setSelectedMode] = useState<CRMConversationMode>(
    CRMConversationMode.allChats
  );
  const { isCx } = useAppVariant();

  const { searchQuery, reset, inputProps } = useSearchInput();
  const {
    selectedChannel,
    selectChannel,
    selectConversation,
    availableChannels,
    incomingWhatsappMessages$,
  } = useClientSupportPageContext();
  const { openManageAccountDrawer } = useManageAccountsDrawer();

  const [isChannelPopoverOpen, setIsChannelPopoverOpen] = useState(false);
  const [hasNewMessageInOtherChannel, setHasNewMessageInOtherChannel] =
    useState(false);

  const { unreadCounts, refetch: refetchUnreadCounts } =
    useCRMUnreadIndicators();

  useEffect(() => {
    if (isChannelPopoverOpen) {
      refetchUnreadCounts();
      setHasNewMessageInOtherChannel(false);
    }
  }, [isChannelPopoverOpen, refetchUnreadCounts]);

  // Subscribe to conversation subjects for other channels
  useEffect(() => {
    const subscriptions = [
      incomingWhatsappMessages$.subscribe(() => {
        if (selectedChannel !== CRMChannel.whatsapp) {
          setHasNewMessageInOtherChannel(true);
        }
      }),
      instagramMessages$.subscribe((action) => {
        if (
          action.type === "append" &&
          selectedChannel !== CRMChannel.instagram
        ) {
          setHasNewMessageInOtherChannel(true);
        }
      }),
      webChatBotMessages$.subscribe((action) => {
        if (
          action.type === "append" &&
          selectedChannel !== CRMChannel.chatbot
        ) {
          setHasNewMessageInOtherChannel(true);
        }
      }),
      twilioMessages$.subscribe((action) => {
        if (action.type === "append" && selectedChannel !== CRMChannel.twilio) {
          setHasNewMessageInOtherChannel(true);
        }
      }),
    ];
    return () => subscriptions.forEach((sub) => sub.unsubscribe());
  }, [incomingWhatsappMessages$, selectedChannel]);

  const getUnreadCountForChannel = (channel: CRMChannel): number => {
    if (!unreadCounts) return 0;
    switch (channel) {
      case CRMChannel.whatsapp:
        return unreadCounts.wa_unread;
      case CRMChannel.instagram:
        return unreadCounts.ig_unread;
      case CRMChannel.chatbot:
        return unreadCounts.chatbot_unread;
      case CRMChannel.twilio:
        return unreadCounts.twilio_unread;
      default:
        return 0;
    }
  };

  const debouncedSearchQuery = useDebounce(searchQuery) as string;

  const conversationTabs = useMemo(
    () => [
      {
        id: CRMConversationFilter.active,
        label: t({
          id: "client-support-page.active",
          defaultMessage: "Active",
        }),
      },
      {
        id: CRMConversationFilter.expired,
        label: t({
          id: "client-support-page.expired",
          defaultMessage: "Expired",
        }),
      },
      ...(selectedChannel === CRMChannel.whatsapp
        ? [
            {
              id: CRMConversationFilter.blocked,
              label: t({
                id: "client-support-page.blocked",
                defaultMessage: "Blocked",
              }),
            },
          ]
        : []),
    ],
    [selectedChannel, t]
  );

  const conversationModes: ListBoxItem<{
    icon: React.ReactNode;
    label: string;
  }>[] = useMemo(
    () => [
      {
        id: CRMConversationMode.allChats,
        value: {
          icon: <AllChatsIcon />,
          label: t({
            id: "client-support-page.all-chats",
            defaultMessage: "All chats",
          }),
        },
      },
      {
        id: CRMConversationMode.onAIAutoReply,
        value: {
          icon: <AIIcon />,
          label: t({
            id: "client-support-page.on-ai-auto-reply",
            defaultMessage: "On AI Auto-reply",
          }),
        },
      },
      {
        id: CRMConversationMode.waitingResponse,
        value: {
          icon: <SupportIcon />,
          label: t({
            id: "client-support-page.waiting-response",
            defaultMessage: "Waiting response",
          }),
        },
      },
      {
        id: CRMConversationMode.managedByMe,
        value: {
          icon: <SupportIcon />,
          label: t({
            id: "client-support-page.managed-by-me",
            defaultMessage: "Managed by Me",
          }),
        },
      },
    ],
    [t]
  );

  const CRMChannels: ListBoxItem<{
    label: string;
    icon: React.ReactNode;
  }>[] = useMemo(() => {
    const getChannelIcon = (channelId: CRMChannel) => {
      switch (channelId) {
        case CRMChannel.whatsapp:
          return <WhatsappIcon />;
        case CRMChannel.instagram:
          return <InstagramIcon />;
        case CRMChannel.chatbot:
          return <WebIcon />;
        case CRMChannel.twilio:
          return <SmsIcon />;
        default:
          return <SupportIcon />;
      }
    };

    const getChannelLabel = (channelId: CRMChannel) => {
      switch (channelId) {
        case CRMChannel.whatsapp:
          return t({
            id: "client-support-page.whatsapp",
            defaultMessage: "WhatsApp",
          });
        case CRMChannel.instagram:
          return t({
            id: "client-support-page.instagram",
            defaultMessage: "Instagram",
          });
        case CRMChannel.chatbot:
          return t({
            id: "client-support-page.web-chat",
            defaultMessage: "Web Chat",
          });
        case CRMChannel.twilio:
          return t({
            id: "client-support-page.twilio",
            defaultMessage: "Text (SMS)",
          });
        default:
          return t({
            id: "client-support-page.unknown-channel",
            defaultMessage: "Unknown Channel",
          });
      }
    };

    return availableChannels.map((channel) => ({
      id: channel.id,
      value: {
        label: channel.name || getChannelLabel(channel.id),
        icon: getChannelIcon(channel.id),
      },
    }));
  }, [availableChannels, t]);

  return (
    <>
      <div className="border-dark-200 flex h-full w-[440px] flex-col border-y-0 border-l-0 border-r border-solid bg-white">
        <div className="flex items-center gap-2 px-8 py-4">
          <Popover
            className="w-[360px] p-4"
            renderTrigger={({ Trigger, triggerRef, isOpen }) => {
              const foundChannel = CRMChannels.find(
                (channel) => channel.id === selectedChannel
              );
              if (isOpen !== isChannelPopoverOpen) {
                setIsChannelPopoverOpen(isOpen);
              }
              return (
                <Trigger
                  ref={triggerRef}
                  as={InteractiveContainer}
                  className="flex h-8 grow items-center gap-1 outline-none"
                >
                  {foundChannel?.value.icon}
                  <span
                    className="text-dark font-secondary ml-1 max-w-[200px] truncate text-[15px] font-medium"
                    title={foundChannel?.value.label}
                  >
                    {foundChannel?.value.label}
                  </span>
                  <ArrowIcon
                    className={cx(
                      "shrink-0 transition-transform",
                      isOpen && "rotate-180"
                    )}
                  />
                  {hasNewMessageInOtherChannel && (
                    <div>
                      <Lottie
                        options={{
                          animationData: unreadIndicatorAnimation,
                        }}
                        height={24}
                        width={24}
                      />
                    </div>
                  )}
                </Trigger>
              );
            }}
          >
            {({ onClose }) => (
              <>
                <ListBox
                  items={CRMChannels}
                  selectionBehavior={{ mode: "single", canToggle: false }}
                  renderLabel={({ value: { label } }) => label}
                  renderStartIcon={({ value: { icon } }) => icon}
                  renderEndIcon={({ id }) => {
                    const count = getUnreadCountForChannel(id as CRMChannel);
                    if (count === 0) return null;
                    return (
                      <span className="min-w-5 flex h-5 items-center justify-center rounded-full bg-[#09B965] px-1.5 text-xs font-medium text-white">
                        {count}
                      </span>
                    );
                  }}
                  onSelect={({ id }) => {
                    setSelectedFilter(CRMConversationFilter.active);
                    setSelectedMode(CRMConversationMode.allChats);
                    selectChannel(id as CRMChannel);
                    onClose();
                  }}
                  defaultSelectedIds={[selectedChannel]}
                />
                {CRMChannels.length < Object.keys(CRMChannel).length && (
                  <>
                    <div className="bg-grey-200 mb-3 h-px w-full" />
                    <InteractiveContainer
                      className="flex h-12 items-center gap-2 px-3"
                      onClick={() => {
                        openManageAccountDrawer({
                          name: "connectedAccountList",
                        });
                        onClose();
                      }}
                    >
                      <span className="text-primary-800 font-secondary">
                        {t({
                          id: "client-support-page.add-new-channel",
                          defaultMessage: "Add new channel",
                        })}
                      </span>
                      <PlusIcon />
                    </InteractiveContainer>
                  </>
                )}
              </>
            )}
          </Popover>
          {/* <InteractiveContainer
            className="bg-grey-200 hover:bg-grey-300 flex h-8 w-[85px] items-center justify-center gap-2 rounded-lg transition-colors"
            onClick={() => setIsSettingsDialogOpen(true)}
          >
            <FilterIcon />
            <span className="text-dark-800 font-secondary text-sm leading-[21px]">
              {t({
                id: "client-support-page.filters",
                defaultMessage: "Filters",
              })}
            </span>
          </InteractiveContainer> */}
          {!isCx && (
            <Tooltip
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-[linear-gradient(225deg,#E7F3FF_0%,#F4E4FF_100%)] transition-colors after:absolute after:inset-0 after:bg-[#1A2023]/5 after:opacity-0 after:transition-opacity after:content-[''] hover:after:opacity-100"
                  onClick={() =>
                    openManageAccountDrawer({ name: "aiAgentInsights" })
                  }
                  ref={ref}
                  {...props}
                >
                  <InsightsIcon />
                </PlainButton>
              )}
            >
              {t({
                id: "client-support-page.ai-agent-insights",
                defaultMessage: "AI Agent Insights",
              })}
            </Tooltip>
          )}
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className="bg-grey-200 hover:bg-grey-300 flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                onClick={() =>
                  openManageAccountDrawer({ name: "connectedAccountList" })
                }
                ref={ref}
                {...props}
              >
                <SettingsIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "client-support-page.crm settings",
              defaultMessage: "CRM Settings",
            })}
          </Tooltip>
        </div>
        <div className="bg-grey-200 h-px w-full" />
        <div className="flex flex-col gap-3 px-8 py-6">
          <SearchInput
            variant="filled"
            color="grey"
            {...inputProps}
            onReset={reset}
            onClear={reset}
            popover={
              selectedFilter === CRMConversationFilter.active
                ? {
                    renderTrigger: ({ Trigger, triggerRef, isOpen }) => (
                      <Trigger
                        as={InteractiveContainer}
                        ref={triggerRef}
                        className={cx(
                          "flex shrink-0 items-center justify-center outline-none",
                          !inputProps.value.length &&
                            "border-grey-200 h-10 border-0 border-l border-solid pl-2.5"
                        )}
                      >
                        <div className="relative flex items-center justify-center">
                          <FilterIcon />
                          {!!inputProps.value.length &&
                            selectedMode !== CRMConversationMode.allChats && (
                              <div className="bg-gradients-danger absolute right-0 top-0 h-2 w-2 rounded-full border border-solid border-white" />
                            )}
                        </div>
                        {!inputProps.value.length && (
                          <>
                            <span className="text-dark-800 font-secondary ml-2.5 mr-1 text-[13px] leading-[150%] tracking-[0.13px]">
                              {
                                conversationModes.find(
                                  (mode) => mode.id === selectedMode
                                )?.value.label
                              }
                            </span>
                            <ArrowIcon
                              className={cx(
                                "transition-transform",
                                isOpen && "rotate-180"
                              )}
                            />
                          </>
                        )}
                      </Trigger>
                    ),
                    className: "p-4 w-[260px]",
                    children: ({ onClose }) => (
                      <ListBox
                        className="font-secondary text-[14px]"
                        items={conversationModes}
                        selectionBehavior={{ canToggle: true, mode: "single" }}
                        renderLabel={({ value }) => value.label}
                        renderEndIcon={({ id }) => (
                          <Radio readOnly isChecked={id === selectedMode} />
                        )}
                        renderStartIcon={({ value }) => value.icon}
                        defaultSelectedIds={[selectedMode]}
                        onSelect={({ id }) => {
                          setSelectedMode(id as CRMConversationMode);
                          onClose();
                        }}
                      />
                    ),
                  }
                : undefined
            }
          />
          {(selectedChannel === CRMChannel.whatsapp ||
            selectedChannel === CRMChannel.instagram) && (
            <div className="flex basis-0 gap-2">
              {conversationTabs.map((tab) => (
                <ConversationTab
                  key={tab.id}
                  label={tab.label}
                  isSelected={selectedFilter === tab.id}
                  onSelect={() => {
                    setSelectedFilter(tab.id);
                    selectConversation(undefined);
                  }}
                />
              ))}
            </div>
          )}
        </div>
        <div className="jugl__custom-scrollbar flex flex-1 basis-0 flex-col overflow-y-auto pb-px">
          {selectedChannel === CRMChannel.whatsapp ? (
            <WhatsappConversations
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              selectedMode={selectedMode}
              searchQuery={debouncedSearchQuery}
            />
          ) : selectedChannel === CRMChannel.chatbot ? (
            <WebChatBotConversations
              searchQuery={debouncedSearchQuery}
              selectedMode={selectedMode}
            />
          ) : selectedChannel === CRMChannel.twilio ? (
            <TwilioConversations
              searchQuery={debouncedSearchQuery}
              selectedMode={selectedMode}
            />
          ) : (
            <InstagramConversations
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              searchQuery={debouncedSearchQuery}
              selectedMode={selectedMode}
            />
          )}
        </div>
      </div>
    </>
  );
};
